import '../../journey.scss';
import './driverDetails.scss';

import { Button } from '@transferz/fe-component-library/src/components/button/Button';
import { CarIcon, PhoneIcon } from '@transferz/fe-component-library/src/components/icons';
import Car from '@transferz/fe-component-library/src/components/icons/CarIcon';
import More from '@transferz/fe-component-library/src/components/icons/MoreIcon';
import PhoneRing from '@transferz/fe-component-library/src/components/icons/PhoneRingIcon';
import Profile from '@transferz/fe-component-library/src/components/icons/ProfileIcon';
import User from '@transferz/fe-component-library/src/components/icons/UserIcon';
import Notification from '@transferz/fe-component-library/src/components/notification/Notification';
import Typography from '@transferz/fe-component-library/src/components/typography/Typography';
import { NotificationVariants } from '@transferz/fe-component-library/src/types/notification';
import { JourneyStatusType } from '@transferz/fe-models';
import { useLocalize } from 'localize-react';
import React, { useMemo, useRef, useState } from 'react';
import { useInterval } from 'usehooks-ts';

import { convertToTimezoneAndFormat, getDateDifference } from '../../../../utils/utils';

export const DriverDetails: React.FC<any> = ({ journey }) => {
  const { translate } = useLocalize();
  // Workaround for not showing the card at all when no content is available
  const contentRef = useRef<HTMLDivElement>(null);

  const isCancelled = journey.status === JourneyStatusType.CancelledFree || journey.status === JourneyStatusType.CancelledWithCosts;
  const driverPhotoExternalKey = journey?.driver?.photoFileExternalKey;
  const driverPhoto = driverPhotoExternalKey ? `${window.tz_globalConfigs.storageFilesDownloadUrl}/download?externalKey=${driverPhotoExternalKey}` : '';
  const pickupTimeZone = journey?.journeyExecutionInfo?.pickupTimeZone;
  const isFAQCompleted = journey.status === JourneyStatusType.JourneyInProgress
    || journey.status === JourneyStatusType.Completed
    || journey.status === JourneyStatusType.CancelledFree
    || journey.status === JourneyStatusType.CancelledWithCosts;
  const isCompleted = journey.status === JourneyStatusType.Completed;

  const driverArrivedDate = useMemo(() => {
    if (journey.statusChangeHistory) {
      const driverArrived = journey.statusChangeHistory.find((status: any) => status.newStatus === JourneyStatusType.DriverArrived);
      return driverArrived?.created as string;
    }
    return null;
  }, [journey.statusChangeHistory]);

  const [timeSinceDriverArrivedInMinutes, setTimeSinceDriverArrivedInMinutes] = useState(() => journey?.status === JourneyStatusType.DriverArrived && driverArrivedDate
    ? getDateDifference(driverArrivedDate) || 0
    : 0,
  );

  useInterval(() => {
    if (!document.hidden && journey?.status === JourneyStatusType.DriverArrived) {
      setTimeSinceDriverArrivedInMinutes(getDateDifference(driverArrivedDate) || 0);
    }
  }, Number(process.env.REACT_APP_REFRESH_INTERVAL ?? 15000));

  return (
    <div data-testid="test-driver-details"
      className={`driver-details card-wrap ${journey?.driver?.name ? 'driver' : ''} ${isFAQCompleted ? 'faq-completed' : ''} ${isCompleted ? 'completed' : ''}`}
      style={{ display: contentRef.current?.hasChildNodes() ? 'initial' : 'none' }}
    >
      {isCancelled
        ? null
        : (
          <div ref={contentRef} className={`driver-card ${journey?.driver?.name ? 'm-t-16' : ''}`}>
            {(journey?.status === JourneyStatusType.DriverUnderway || journey?.status === JourneyStatusType.DriverArrived) && (
              <Notification
                variant={NotificationVariants.neutral}
                message={journey?.status === JourneyStatusType.DriverUnderway ? translate('n_driverUnderway')
                  : journey?.status === JourneyStatusType.DriverArrived ? translate('n_driverArrived', { minutes: timeSinceDriverArrivedInMinutes, localTime: convertToTimezoneAndFormat(driverArrivedDate!, pickupTimeZone) }) : ''}
                closeIcon={false}
                icon={<CarIcon fill="#0083A8" />}
                className="m-t-16 m-b-16"
              />
            )}
            {(journey?.driver?.mobilePhoneNumber || journey?.driver?.name) && (
              <div className="d-flex mb-24">
                <User />
                <Typography variant="body1">
                  {journey?.driver?.name || translate('b_unknownDriver')}
                </Typography>
                {driverPhoto
                  ? (
                    <div className="image-container">
                      <img src={driverPhoto} />
                    </div>
                  )
                  : (
                    <div className="image-container placeholder">
                      <Profile width={50} />
                    </div>
                  )
                }
              </div>
            )}
            {journey?.vehicle?.vehicleMake && (
              <div className="d-flex mb-24">
                <Car />
                <Typography variant="body1">
                  {journey?.vehicle?.vehicleMake} {journey?.vehicle?.vehicleModel ?? ''}
                </Typography>
              </div>
            )}
            {journey?.vehicle?.licensePlate && (
              <div className="d-flex mb-24">
                <More height="20" />
                <Typography variant="body1">
                  {journey?.vehicle?.licensePlate}
                </Typography>
              </div>
            )}
            {journey?.driver?.mobilePhoneNumber && (
              <div className={`d-flex align-items-center call-driver-button-wrapper ${!journey?.vehicle?.id && 'no-vehicle'}`}>
                <PhoneIcon className="phone-icon" />
                <a href={`tel:${journey?.driver?.mobilePhoneNumber}`} className="call-link">
                  <Button
                    priority="primary"
                    variant="info"
                    label={translate('callDriver')}
                    icon={<PhoneRing />}
                    iconPosition="left"
                    size="lg"
                  />
                </a>
              </div>
            )}
          </div>
        )
      }
    </div>
  );
};
